/**
 * True render (docs/17-asset-library/14-design-studio.md §12): one fully lit
 * picture of an object, a look, a surface, artwork or a design, from the
 * angle on screen. The price comes first (`useStillEstimate`); `useStill`
 * asks for the picture and then asks for its state every 2 s until it has
 * ended. The true render of an ad is another thing (`api/outputs.ts`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ApiError, api } from "./client";
import { ME_KEY } from "./me";
import type { AssetKind } from "./types";

export type StillSize = 768 | 1024;

/** What is pictured: a step of a design, or something of the 3D library. */
export type StillTarget =
  { model: string; revision: number } | { kind: AssetKind; id: string };

export interface StillCamera {
  azimuth_deg: number;
  elevation_deg: number;
  distance_factor: number;
}

/** The body of `POST /v1/assetlib/stills`. Without a `camera` the picture is
 *  taken from the angle the 3D view opens with. */
export interface StillRequest {
  target: StillTarget;
  dressing: {
    materials: Record<string, string>;
    skins: Record<string, string>;
    colors_hex: Record<string, string>;
  };
  poses: Record<string, number>;
  camera?: StillCamera;
  size: StillSize;
}

export type StillState = "queued" | "running" | "done" | "failed" | "cancelled";

/** `GET /v1/assetlib/stills/{id}`. `url` only when `done`. A true render of
 *  an object is drawn on a CPU node: it is free (§15.1). */
export interface Still {
  state: StillState;
  url: string | null;
  seconds: number;
  error_code: string | null;
  size: number;
}

/** How long a true render of a size usually takes (it is free, §15.1). */
export interface StillEstimate {
  seconds: number;
}

export const STILL_ESTIMATE_KEY = (size: StillSize) =>
  ["assetlib", "stills", "estimate", size] as const;
export const STILL_KEY = (id: string) => ["assetlib", "stills", id] as const;

/** How often a picture on its way is asked for. */
export const STILL_POLL_MS = 2_000;

const ended = (state: StillState) =>
  state === "done" || state === "failed" || state === "cancelled";

/** `GET /v1/assetlib/stills/estimate?size=`: what a picture of that size
 *  costs at most and usually, and how long it usually takes. */
export function useStillEstimate(size: StillSize) {
  return useQuery({
    queryKey: STILL_ESTIMATE_KEY(size),
    queryFn: () =>
      api<StillEstimate>(`/v1/assetlib/stills/estimate?size=${size}`),
    staleTime: 60_000,
  });
}

/** One true render at a time: `start(body)` asks for it, `still` is its
 *  state (asked for every 2 s until it is done, failed or stopped), `cancel`
 *  stops it. `error` is a refused request. A stop that arrives after the end
 *  is not an error: the state is asked for again and shown as it ended. */
export function useStill() {
  const queryClient = useQueryClient();
  const [id, setId] = useState<string | null>(null);

  const begin = useMutation({
    mutationFn: (body: StillRequest) =>
      api<{ still_id: string }>("/v1/assetlib/stills", {
        method: "POST",
        body,
      }),
    onMutate: () => setId(null),
    onSuccess: (reply) => setId(reply.still_id),
  });

  const query = useQuery({
    queryKey: STILL_KEY(id ?? ""),
    queryFn: () => api<Still>(`/v1/assetlib/stills/${encodeURIComponent(id!)}`),
    enabled: id !== null,
    refetchInterval: (q) =>
      q.state.data && ended(q.state.data.state) ? false : STILL_POLL_MS,
  });
  const { refetch } = query;

  const stop = useMutation({
    mutationFn: (stillId: string) =>
      api<{ state: StillState }>(
        `/v1/assetlib/stills/${encodeURIComponent(stillId)}/cancel`,
        { method: "POST" },
      ),
    onSettled: () => void refetch(),
  });

  // A picture that ended: the usual wait is asked for again.
  const state = query.data?.state;
  const over = state !== undefined && ended(state);
  useEffect(() => {
    if (!over) return;
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
    void queryClient.invalidateQueries({
      queryKey: ["assetlib", "stills", "estimate"],
    });
  }, [over, queryClient]);

  const stopError =
    stop.error instanceof ApiError && stop.error.code === "invalid_state"
      ? null
      : stop.error;

  return {
    start: (body: StillRequest) => {
      stop.reset();
      begin.mutate(body);
    },
    /** null until the request was accepted and its state arrived. */
    still: id !== null ? (query.data ?? null) : null,
    cancel: () => {
      if (id !== null && !stop.isPending) stop.mutate(id);
    },
    /** A request that was refused, or a stop that failed. */
    error: begin.error ?? stopError ?? null,
    /** From the click until the picture has ended. */
    pending:
      begin.isPending ||
      (id !== null && begin.error === null && !(query.data && over)),
  };
}
